import { DimensionCategory, DimensionFieldType, DimensionSchemaConfig, GeneratedSchema } from '../types/dimension.types';
import { Calendar, MapPin, Briefcase, Building, Users, TrendingUp, HardHat, Settings } from 'lucide-react';

/**
 * Get icon component for category
 */
export function getCategoryIcon(category: DimensionCategory) {
  const icons = {
    temporal: <Calendar className="h-5 w-5 text-blue-500" />,
    geographic: <MapPin className="h-5 w-5 text-green-500" />,
    government: <Briefcase className="h-5 w-5 text-purple-500" />,
    organizational: <Building className="h-5 w-5 text-orange-500" />,
    demographic: <Users className="h-5 w-5 text-pink-500" />,
    economic: <TrendingUp className="h-5 w-5 text-yellow-500" />,
    infrastructure: <HardHat className="h-5 w-5 text-amber-500" />,
    custom: <Settings className="h-5 w-5 text-gray-500" />,
  };
  return icons[category] || icons.custom;
}

/**
 * Get color class for category
 */
export function getCategoryColor(category: DimensionCategory): string {
  const colors = {
    temporal: 'bg-blue-500',
    geographic: 'bg-green-500',
    government: 'bg-purple-500',
    organizational: 'bg-orange-500',
    demographic: 'bg-pink-500',
    economic: 'bg-yellow-500',
    infrastructure: 'bg-amber-500',
    custom: 'bg-gray-500',
  };
  return colors[category] || colors.custom;
}

/**
 * Get SQL type from dimension field type
 */
export function getSQLType(field: { type: DimensionFieldType; length?: number; precision?: number; scale?: number; values?: string[] }): string {
  switch (field.type) {
    case 'string':
      return `VARCHAR(${field.length || 255})`;
    case 'text':
      return 'TEXT';
    case 'integer':
      return 'INTEGER';
    case 'decimal':
      return `DECIMAL(${field.precision || 18}, ${field.scale || 2})`;
    case 'boolean':
      return 'BOOLEAN';
    case 'date':
      return 'DATE';
    case 'datetime':
      return 'TIMESTAMP(3)';
    case 'json':
      return 'JSONB';
    case 'enum':
      // PostgreSQL doesn't have native ENUM in this context, use VARCHAR with CHECK constraint
      return `VARCHAR(50)`;
    default:
      return 'VARCHAR(255)';
  }
}

/**
 * Generate Prisma schema for a dimension
 */
export function generatePrismaSchema(dimension: DimensionSchemaConfig): string {
  const tableName = dimension.tableName || `dim_${dimension.id}`;
  const lines: string[] = [];

  lines.push(`model ${toPascalCase(tableName)} {`);

  // Primary key
  lines.push(`  id              String          @id @default(uuid())`);

  // Fields
  dimension.fields.forEach(field => {
    const prismaType = getPrismaType(field);
    const required = field.required ? '' : '?';
    const defaultVal = field.defaultValue !== undefined ? ` @default(${JSON.stringify(field.defaultValue)})` : '';
    const unique = field.unique ? ' @unique' : '';
    const map = field.name !== camelCase(field.name) ? ` @map("${field.name}")` : '';

    lines.push(`  ${camelCase(field.name)}${padRight(camelCase(field.name), 20)} ${prismaType}${required}${defaultVal}${unique}${map}`);
  });

  // Metadata field (always present for flexibility)
  lines.push(`  metadata        Json?`);

  // Indexes
  if (dimension.indexes) {
    lines.push('');
    dimension.indexes.forEach(idx => {
      const fields = idx.fields.map(f => `"${camelCase(f)}"`).join(', ');
      lines.push('  @@index([' + fields + ']' + (idx.unique ? ', unique: true' : '') + ', name: "' + idx.name + '")');
    });
  }

  // Auto-indexes for indexed fields
  const indexedFields = dimension.fields.filter(f => f.indexed);
  if (indexedFields.length > 0) {
    lines.push('');
    indexedFields.forEach(field => {
      lines.push('  @@index(["' + camelCase(field.name) + '"], name: "' + tableName + '_' + field.name + '_idx")');
    });
  }

  // Table name mapping
  lines.push('  @@map("' + tableName + '")');
  lines.push('}');

  return lines.join('\n');
}

/**
 * Generate CREATE TABLE SQL for a dimension
 */
export function generateCreateTableSQL(dimension: DimensionSchemaConfig): { sql: string; constraints: string[] } {
  const tableName = dimension.tableName || `dim_${dimension.id}`;
  const lines: string[] = [];
  const constraints: string[] = [];

  lines.push(`CREATE TABLE "${tableName}" (`);
  lines.push(`    "id" UUID PRIMARY KEY DEFAULT gen_random_uuid(),`);

  // Fields
  const fieldSQL: string[] = [];
  dimension.fields.forEach(field => {
    const sqlType = getSQLType(field);
    const notNull = field.required ? ' NOT NULL' : '';
    const unique = field.unique ? ' UNIQUE' : '';
    const defaultVal = field.defaultValue !== undefined ? ` DEFAULT ${escapeDefault(field.defaultValue, field.type)}` : '';

    fieldSQL.push(`    "${field.name}" ${sqlType}${notNull}${unique}${defaultVal}`);

    // Generate CHECK constraint for enum values
    if (field.type === 'enum' && field.values) {
      const values = field.values.map(v => `'${v}'`).join(', ');
      constraints.push(
        `ALTER TABLE "${tableName}" ADD CONSTRAINT "${tableName}_${field.name}_check" CHECK ("${field.name}" IN (${values}));`
      );
    }
  });

  // Metadata field
  fieldSQL.push(`    "metadata" JSONB`);

  lines.push(fieldSQL.join(',\n'));
  lines.push(');');

  // Indexes
  const indexSQL: string[] = [];
  const indexedFields = dimension.fields.filter(f => f.indexed);
  indexedFields.forEach(field => {
    indexSQL.push(`CREATE INDEX "${tableName}_${field.name}_idx" ON "${tableName}"("${field.name}");`);
  });

  // Custom indexes
  if (dimension.indexes) {
    dimension.indexes.forEach(idx => {
      const fields = idx.fields.map(f => `"${f}"`).join(', ');
      indexSQL.push(
        `CREATE ${idx.unique ? 'UNIQUE ' : ''}INDEX "${idx.name}" ON "${tableName}" (${fields});`
      );
    });
  }

  return {
    sql: lines.join('\n'),
    constraints,
  };
}

/**
 * Generate complete schema output
 */
export function generateCompleteSchema(dimension: DimensionSchemaConfig): GeneratedSchema {
  const prismaSchema = generatePrismaSchema(dimension);
  const { sql: createTableSQL, constraints } = generateCreateTableSQL(dimension);
  const createIndexSQL = constraints;

  return {
    prismaSchema,
    createTableSQL,
    createIndexSQL,
  };
}

/**
 * Validate dimension schema
 */
export function validateDimension(dimension: DimensionSchemaConfig): { valid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required fields
  if (!dimension.id || dimension.id.trim() === '') {
    errors.push('Dimension ID is required');
  }

  if (!dimension.name || dimension.name.trim() === '') {
    errors.push('Dimension name is required');
  }

  if (!dimension.fields || dimension.fields.length === 0) {
    errors.push('Dimension must have at least one field');
  }

  // Validate field names
  const fieldNames = new Set<string>();
  dimension.fields?.forEach((field, index) => {
    if (!field.name || field.name.trim() === '') {
      errors.push(`Field at index ${index} is missing a name`);
    } else {
      // Check for duplicate field names
      const normalizedName = field.name.toLowerCase();
      if (fieldNames.has(normalizedName)) {
        errors.push(`Duplicate field name: ${field.name}`);
      }
      fieldNames.add(normalizedName);

      // Check field name format (snake_case preferred)
      if (!/^[a-z][a-z0-9_]*$/.test(field.name)) {
        warnings.push(`Field "${field.name}" should use snake_case format`);
      }
    }

    // Validate enum values
    if (field.type === 'enum') {
      if (!field.values || field.values.length === 0) {
        errors.push(`Field "${field.name}" is enum type but has no values defined`);
      }
    }

    // Validate decimal precision/scale
    if (field.type === 'decimal') {
      if (!field.precision || field.precision < 1) {
        warnings.push(`Field "${field.name}": decimal precision should be at least 1`);
      }
      if (field.scale === undefined || field.scale < 0) {
        warnings.push(`Field "${field.name}": decimal scale should be at least 0`);
      }
      if (field.scale! > field.precision!) {
        errors.push(`Field "${field.name}": decimal scale cannot be greater than precision`);
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// Helper functions

function toPascalCase(str: string): string {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

function camelCase(str: string): string {
  const words = str.split('_');
  return words[0] + words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
}

function padRight(str: string, length: number): string {
  return str.padEnd(length, ' ');
}

function getPrismaType(field: { type: DimensionFieldType; length?: number; precision?: number; scale?: number }): string {
  switch (field.type) {
    case 'string':
      return 'String';
    case 'text':
      return 'String';
    case 'integer':
      return 'Int';
    case 'decimal':
      return `Decimal`;
    case 'boolean':
      return 'Boolean';
    case 'date':
      return 'DateTime';
    case 'datetime':
      return 'DateTime';
    case 'json':
      return 'Json';
    case 'enum':
      return 'String';
    default:
      return 'String';
  }
}

function escapeDefault(value: any, type: DimensionFieldType): string {
  switch (type) {
    case 'string':
    case 'enum':
      return `'${value}'`;
    case 'boolean':
      return value ? 'true' : 'false';
    case 'integer':
    case 'decimal':
      return String(value);
    default:
      return `'${value}'`;
  }
}
